import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, Dialog, ErrorText, Field, Input, PageHeader, Pagination, Spinner, Switch } from "../components/ui.tsx";
import { AudienceBadge } from "../components/knowledge.tsx";
import { keepPreviousData, usePaging } from "../hooks.ts";
import { ScanNotes, statusBadge, Viewer } from "./Skills.tsx";

type Scope = "all" | "official" | "company" | "group";

export function MySkillsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-skills", "settings"], queryFn: () => unwrap(api.me.skills.$get()) });
  // Both lists are paged and searchable: a company can have hundreds of skills.
  const minePaging = usePaging([], 10);
  const [scope, setScope] = useState<Scope>("all");
  const sharedPaging = usePaging([scope], 10);
  const mineList = useQuery({
    queryKey: ["my-skills", "mine", minePaging.page, minePaging.pageSize, minePaging.q],
    queryFn: () => unwrap(api.me.skills.mine.$get({ query: { page: String(minePaging.page), pageSize: String(minePaging.pageSize), ...(minePaging.q && { q: minePaging.q }) } })),
    placeholderData: keepPreviousData,
  });
  const sharedList = useQuery({
    queryKey: ["my-skills", "shared", scope, sharedPaging.page, sharedPaging.pageSize, sharedPaging.q],
    queryFn: () => unwrap(api.me.skills.shared.$get({ query: { scope, page: String(sharedPaging.page), pageSize: String(sharedPaging.pageSize), ...(sharedPaging.q && { q: sharedPaging.q }) } })),
    placeholderData: keepPreviousData,
  });
  useEffect(() => void minePaging.clamp(mineList.data?.total), [mineList.data?.total]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => void sharedPaging.clamp(sharedList.data?.total), [sharedList.data?.total]); // eslint-disable-line react-hooks/exhaustive-deps
  const [viewing, setViewing] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const privacy = useMutation({
    mutationFn: (a: { id: string; private: boolean }) => unwrap(api.me.skills[":id"].private.$post({ param: { id: a.id }, json: { private: a.private } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-skills"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.me.skills[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-skills"] }),
  });
  const refresh = useMutation({
    mutationFn: () => unwrap(api.me.skills.refresh.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-skills"] }),
  });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { sharingEnabled, level, groupName } = q.data;
  const mine = mineList.data?.items ?? [];
  const shared = sharedList.data?.items ?? [];
  const sharing = sharingEnabled && level !== "off";
  const team = groupName ?? "your group";

  return (
    <>
      <PageHeader
        title="Skills"
        description="Skills are step-by-step know-how your assistant saves and reuses. Ask it to “save this as a skill” after it gets something right."
      />

      <div className="mb-1 flex items-center gap-3">
        <h2 className="flex-1 text-base font-semibold">Your skills</h2>
        {refresh.data && !refresh.isPending && <span className="text-xs text-[var(--muted)]">Up to date</span>}
        <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? "Checking…" : "Refresh"}</Button>
      </div>
      <p className="mb-3 text-sm text-[var(--muted)]">
        {!sharingEnabled
          ? "Skills your assistant creates stay in your assistant — sharing skills is switched off."
          : level === "off"
            ? "Skills your assistant creates stay in your assistant — your group doesn't share skills."
            : level === "group"
              ? `Skills your assistant creates are checked for sensitive data (passwords, personal details, confidential client data) and then shared with the rest of ${team} — never company-wide. Keep any skill private with the switch.`
              : `Skills your assistant creates are checked for sensitive data (passwords, personal details, confidential client data) and then shared — with the rest of ${team} when they are about the team's own work, otherwise with the whole company. Keep any skill private with the switch.`}
      </p>
      <ErrorText error={privacy.error ?? remove.error ?? refresh.error ?? mineList.error} />
      {(minePaging.q || (mineList.data?.total ?? 0) > minePaging.pageSize) && (
        <Input value={minePaging.search} onChange={(e) => minePaging.setSearch(e.target.value)} placeholder="Search your skills…" className="mb-3 max-w-sm" />
      )}
      <Card className="divide-y divide-[var(--line)]">
        {mineList.isPending && <div className="grid place-items-center py-6"><Spinner /></div>}
        {mineList.data && mine.length === 0 && (
          <div className="px-5 py-6 text-sm text-[var(--muted)]">{minePaging.q ? "No skills match that search." : "None yet. Ask your assistant to “save this as a skill”, then press Refresh."}</div>
        )}
        {mine.map((s) => (
          <div key={s.id} className="flex flex-wrap items-start gap-4 px-5 py-3">
            <div className="min-w-0 flex-1 basis-60">
              <div className="flex flex-wrap items-center gap-2">
                <button className="font-mono text-sm font-medium hover:underline" onClick={() => setViewing(s.id)}>{s.name}</button>
                <Badge tone={statusBadge[s.status].tone}>{s.status === "blocked" ? "Not shared: sensitive" : s.status === "unshared" ? "Only in your assistant" : statusBadge[s.status].label}</Badge>
                {(s.status === "shared" || s.status === "pending") && <AudienceBadge audience={s.audience} own />}
                {s.status === "shared" && !s.live && <Badge>A colleague's skill has this name</Badge>}
              </div>
              {s.description && <div className="mt-0.5 text-sm text-[var(--muted)]">{s.description}</div>}
              <ScanNotes scan={s.scan} />
            </div>
            {sharing && s.status !== "hidden" && (
              <div className="w-36">
                <Switch label="Keep private" checked={s.status === "private"} disabled={privacy.isPending} onChange={(v) => privacy.mutate({ id: s.id, private: v })} />
              </div>
            )}
            <Button variant="ghost" className="h-8" onClick={() => setEditing({ id: s.id, name: s.name })}>
              Edit
            </Button>
            <Button
              variant="ghost"
              className="h-8 text-red-700 hover:bg-red-500/10"
              disabled={remove.isPending}
              onClick={() =>
                confirm(`Delete the skill “${s.name}”? Your assistant stops using it${s.status === "shared" ? " and colleagues stop getting it" : ""}. A copy is kept in your assistant's archive.`) &&
                remove.mutate(s.id)
              }
            >
              Delete
            </Button>
          </div>
        ))}
      </Card>
      <div className="mb-10">
        <Pagination page={minePaging.page} pageSize={minePaging.pageSize} total={mineList.data?.total ?? 0} onPage={minePaging.setPage} onPageSize={minePaging.setPageSize} busy={mineList.isFetching} />
      </div>

      <h2 className="mb-1 text-base font-semibold">Shared with you</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">
        Your assistant already has these: official skills from your administrator (they take precedence), skills shared with the whole company, and skills colleagues in {team} shared within the group.
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(
          [
            ["all", "All"],
            ["official", "Official"],
            ["company", "Company"],
            ["group", groupName ? `From ${groupName}` : "Your group"],
          ] as [Scope, string][]
        ).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setScope(id)} className={cx("rounded-full border px-3 py-1 text-sm", scope === id ? "border-[var(--color-accent)] bg-[var(--panel-2)] font-medium" : "border-[var(--line)] text-[var(--muted)] hover:text-[var(--text)]")}>
            {label}
          </button>
        ))}
        {(sharedPaging.q || (sharedList.data?.total ?? 0) > sharedPaging.pageSize) && (
          <Input value={sharedPaging.search} onChange={(e) => sharedPaging.setSearch(e.target.value)} placeholder="Search shared skills…" className="ml-auto max-w-sm" />
        )}
      </div>
      <ErrorText error={sharedList.error} />
      <Card className={cx("divide-y divide-[var(--line)]", sharedList.isPlaceholderData && "opacity-60")}>
        {sharedList.isPending && <div className="grid place-items-center py-6"><Spinner /></div>}
        {sharedList.data && shared.length === 0 && (
          <div className="px-5 py-6 text-sm text-[var(--muted)]">
            {sharedPaging.q ? "No skills match that search." : scope === "group" ? `Nothing shared within ${team} yet.` : scope === "company" ? "Nothing shared company-wide yet." : scope === "official" ? "No official skills yet." : "Nothing shared with you yet."}
          </div>
        )}
        {shared.map((s) => (
          <div key={s.id} className="px-5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <button className="font-mono text-sm font-medium hover:underline" onClick={() => setViewing(s.id)}>{s.name}</button>
              {s.scope === "official" ? <Badge tone="accent">Official</Badge> : <AudienceBadge audience={s.scope} own />}
              {s.author && <span className="text-xs text-[var(--muted)]">by {s.author}</span>}
            </div>
            {s.description && <div className="mt-0.5 text-sm text-[var(--muted)]">{s.description}</div>}
          </div>
        ))}
      </Card>
      <Pagination page={sharedPaging.page} pageSize={sharedPaging.pageSize} total={sharedList.data?.total ?? 0} onPage={sharedPaging.setPage} onPageSize={sharedPaging.setPageSize} busy={sharedList.isFetching} />
      {viewing && <Viewer id={viewing} onClose={() => setViewing(null)} />}
      {editing && (
        <OwnSkillEditor
          id={editing.id}
          name={editing.name}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["my-skills"] });
          }}
        />
      )}
    </>
  );
}

/** Edits one of your own skills, in your assistant. The name stays (it ties the skill to its folder and to colleagues' copies). */
function OwnSkillEditor({ id, name, onClose, onSaved }: { id: string; name: string; onClose: () => void; onSaved: () => void }) {
  const q = useQuery({ queryKey: ["skill-edit", id], queryFn: () => unwrap(api.me.skills[":id"].$get({ param: { id } })), gcTime: 0 });
  const [files, setFiles] = useState<{ path: string; text: string }[] | null>(null);
  const [binary, setBinary] = useState<string[]>([]);
  useEffect(() => {
    if (q.data && !files) {
      setFiles(q.data.files.filter((f) => f.text !== null).map((f) => ({ path: f.path, text: f.text! })));
      setBinary(q.data.files.filter((f) => f.text === null).map((f) => f.path));
    }
  }, [q.data, files]);
  const save = useMutation({
    mutationFn: () => unwrap(api.me.skills[":id"].$put({ param: { id }, json: { files: Object.fromEntries(files!.map((f) => [f.path.trim(), f.text])) } })),
    onSuccess: onSaved,
  });
  const set = (i: number, p: Partial<{ path: string; text: string }>) => setFiles(files!.map((f, j) => (j === i ? { ...f, ...p } : f)));

  return (
    <Dialog open onClose={onClose} title={`Edit ${name}`} wide>
      {q.error ? (
        <ErrorText error={q.error} />
      ) : !files ? (
        <div className="grid place-items-center py-10"><Spinner /></div>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <Field label="Name" hint="Stays the same (it's how your assistant and colleagues find the skill).">
            <Input value={name} disabled className="font-mono" />
          </Field>
          {files.map((f, i) => (
            <div key={i}>
              <div className="mb-1 flex items-center gap-2">
                <Input value={f.path} disabled={f.path === "SKILL.md"} onChange={(e) => set(i, { path: e.target.value })} className="font-mono text-xs" />
                {f.path !== "SKILL.md" && <Button type="button" variant="danger" onClick={() => setFiles(files.filter((_, j) => j !== i))}>Remove</Button>}
              </div>
              <textarea
                rows={f.path === "SKILL.md" ? 16 : 8}
                value={f.text}
                onChange={(e) => set(i, { text: e.target.value })}
                className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
              />
            </div>
          ))}
          {binary.length > 0 && <p className="text-xs text-[var(--muted)]">Kept as is: {binary.join(", ")}</p>}
          <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setFiles([...files, { path: `references/notes-${files.length}.md`, text: "" }])}>
            + Add a file
          </button>
          <p className="text-xs text-[var(--muted)]">Saved straight into your assistant. A shared skill is checked for sensitive data again before colleagues get the new version.</p>
          <ErrorText error={save.error} />
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
